import React, { useState, useMemo, useEffect } from 'react';
import FormSetup from '../Forms/FormSetup';
import { MasterConfigs } from '../../interfaces/entity';
import { constants } from '../../constants/common';
import ProfileCard from '../ContactInfo/ProfileCard';
import { type ReminderConfig } from '../Reminder';
import { type RepeatConfig } from '../Repeat';
import { useDispatch, useSelector } from 'react-redux';
import { postData } from '../../actions/developer';
import { Button, Space, Radio, InputNumber, TimePicker, Select, DatePicker } from 'antd';
import moment from 'moment';
import dayjs, { Dayjs } from 'dayjs';

interface TaskFormModalProps {
    open: boolean;
    onCancel: () => void;
    onSuccess?: () => void;
    recordId: string;
    modelName: string;
    initialValues?: any;
}

const TaskFormModal: React.FC<TaskFormModalProps> = ({
    open,
    onCancel,
    onSuccess,
    recordId,
    modelName,
    initialValues
}) => {
    const [refType, setRefType] = useState<string>(null);
    const [formInstance, setFormInstance] = useState<any>(null);
    const [enableReminder, setEnableReminder] = useState(false);
    const [enableRepeat, setEnableRepeat] = useState(false);

    // Reminder form state
    const [reminderMode, setReminderMode] = useState<'on' | 'before'>('before');
    const [reminderDaysBefore, setReminderDaysBefore] = useState<number>(0);
    const [reminderTime, setReminderTime] = useState<Dayjs>(dayjs('09:00', 'HH:mm'));
    const [reminderAlertType, setReminderAlertType] = useState<'email' | 'popup' | 'both'>('both');

    // Repeat form state
    const [repeatType, setRepeatType] = useState<'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom'>('daily');
    const [repeatInterval, setRepeatInterval] = useState<number>(1);
    const [repeatDaysOfWeek, setRepeatDaysOfWeek] = useState<number[]>([]);
    const [repeatEnds, setRepeatEnds] = useState<'never' | 'after' | 'on'>('never');
    const [repeatOccurrences, setRepeatOccurrences] = useState<number>(1);
    const [repeatEndDate, setRepeatEndDate] = useState<Dayjs | null>(null);
    const dispatch = useDispatch();
    const currentUser = useSelector((state: any) => state?.app?.user);

    // Map model names to refType
    const getRefType = (modelName: string): string => {
        const mapping: { [key: string]: string } = {
            'leads': 'Leads',
            'contacts': 'Contacts',
            'accounts': 'Businesses',
            'deals': 'Deals',
        };
        return mapping[modelName.toLowerCase()] || modelName;
    };

    const mappedRefType = useMemo(() => getRefType(modelName), [modelName]);

    // Set refType when modelName changes
    useEffect(() => {
        if (mappedRefType) {
            setRefType(mappedRefType);
        }
    }, [mappedRefType]);

    const config: MasterConfigs = useMemo(() => ({
        api: () => `/tasks-management`,
        title: "Create Task",
        modalTitle: () => 'Create Task',
        modalProps: {
            maskClosable: false,
        },
        getFormColumns: () => {
            const formColumns: any[] = [
                {
                    label: 'Task Owner',
                    name: 'taskOwner',
                    type: 'select',
                    isCombo: true,
                    rules: [{ required: true, message: 'Task Owner is required' }],
                    commonProps: {
                        initialValue: currentUser?._id || undefined
                    },
                    extraOptions: currentUser?._id ? [{
                        value: currentUser._id,
                        name: constants.GET_FULL_NAME(currentUser) || 'Current User',
                        label: constants.GET_FULL_NAME(currentUser) || 'Current User'
                    }] : []
                },
                {
                    span: 16,
                },
                {
                    label: "Subject",
                    name: "subject",
                    type: "text",
                    span: 16,
                    rules: [{ required: true, message: 'Subject is required' }]
                },
                {
                    label: "Due Date",
                    name: "dueDate",
                    type: "date",
                    rules: [{ required: true, message: 'Due Date is required' }]
                },
                {
                    label: 'Task For',
                    name: 'refType',
                    type: 'select',
                    children: constants.TASK_FOR.map(type => ({ name: type, value: type })),
                    selectProps: {
                        onChange: (value) => {
                            setRefType(value);
                            if (formInstance) {
                                formInstance.setFieldsValue({ refId: undefined });
                            }
                        }
                    },
                    commonProps: {
                        disabled: !!mappedRefType,
                        initialValue: mappedRefType
                    },
                    rules: [{ required: true, message: 'Task For is required' }]
                },
                {
                    label: "Task For ID",
                    name: "refId",
                    type: "select",
                    isCombo: true,
                    commonProps: {
                        initialValue: recordId
                    },
                    rules: [{ required: true, message: 'Task For ID is required' }],
                },
                {
                    label: "Priority",
                    name: "priority",
                    type: "select",
                    children: constants.PRIORITY.map(priority => ({ name: priority, value: priority })),
                    commonProps: {
                        initialValue: 'Normal'
                    }
                },
                {
                    label: "Task Status",
                    name: "taskStatus",
                    type: "select",
                    children: constants.TASK_STATUS.map(status => ({ name: status, value: status })),
                    commonProps: {
                        initialValue: 'Not Started'
                    }
                },
                {
                    span: 16,
                },
                {
                    label: "Description",
                    name: "description",
                    type: "textarea",
                    span: 16
                },
            ];

            return formColumns;
        },
        combo: [
            {
                api: '/users',
                name: 'taskOwner',
                idKey: '_id',
                render: (text) => <ProfileCard
                    size={30}
                    name={constants.GET_FULL_NAME(text)}
                    avatarUrl={text?.avatar ? process.env.FILE_URL + text?.avatar : null}
                    role={null}
                />
            },
            refType && {
                api: `/tasks-management/ref-types/list?refType=${refType}`,
                name: 'refId',
                idKey: '_id',
                render: (text) => text.name,
                rerender: [refType]
            }
        ].filter(Boolean),
        onSubmit: async (id: string | null, values: any) => {
            const taskData: any = {
                ...values,
                refType: mappedRefType || values.refType,
                refId: recordId || values.refId,
            };

            // Build reminder config from form values
            if (enableReminder && values.reminderMode) {
                const reminderConfig: ReminderConfig = {
                    mode: values.reminderMode,
                    time: values.reminderTime ? dayjs(values.reminderTime).format('HH:mm') : '09:00',
                    alertType: values.reminderAlertType || 'both',
                };
                if (values.reminderMode === 'before' && values.reminderDaysBefore !== undefined) {
                    reminderConfig.daysBefore = values.reminderDaysBefore;
                }
                taskData.reminder = reminderConfig;
            }

            // Build repeat config from form values
            if (enableRepeat && values.repeatType) {
                const repeatConfig: RepeatConfig = {
                    type: values.repeatType,
                    interval: values.repeatInterval || 1,
                    ends: values.repeatEnds || 'never',
                };
                if (values.repeatType === 'weekly' && values.repeatDaysOfWeek) {
                    repeatConfig.daysOfWeek = Array.isArray(values.repeatDaysOfWeek) ? values.repeatDaysOfWeek : [1];
                }
                if (values.repeatEnds === 'after' && values.repeatOccurrences) {
                    repeatConfig.occurrences = values.repeatOccurrences;
                } else if (values.repeatEnds === 'on' && values.repeatEndDate) {
                    repeatConfig.endDate = dayjs(values.repeatEndDate).toDate();
                }
                taskData.repeatRule = repeatConfig;
            }

            const apiUrl = config.api();
            const resData = await dispatch(postData(apiUrl, taskData));

            if (resData && resData.code === 200) {
                if (onSuccess) {
                    onSuccess();
                }
            }

            return resData;
        },
        afterSubmit: () => {
            onCancel();
        }
    }), [recordId, modelName, mappedRefType, refType, formInstance, dispatch, currentUser, enableReminder, enableRepeat]);

    const formatReminderText = (config: ReminderConfig, dueDate?: Date | string | null): string => {
        if (!config) return '';
        const timeStr = moment(config.time, 'HH:mm').format('hh:mm A');
        const alertStr = config.alertType === 'both' ? 'Email and Pop-up' : config.alertType === 'email' ? 'Email' : 'Pop-up';

        if (config.mode === 'before') {
            const days = config.daysBefore === 0 ? 'due date' : `${config.daysBefore} day(s) before due date`;
            return `On ${days} at ${timeStr} by ${alertStr}`;
        } else {
            return `On due date at ${timeStr} by ${alertStr}`;
        }
    };

    const formatRepeatText = (config: RepeatConfig): string => {
        if (!config) return '';

        let text = '';
        const interval = config.interval || 1;

        if (config.type === 'daily') {
            text = `Every ${interval} day(s)`;
        } else if (config.type === 'weekly') {
            const days = config.daysOfWeek?.map(d => weekDays.find(w => w.value === d)?.full).filter(Boolean).join(', ') || '';
            text = `Every ${interval} week(s) on ${days}`;
        } else if (config.type === 'monthly') {
            if (config.dayOfMonth) {
                text = `Every ${interval} month(s) on day ${config.dayOfMonth}`;
            } else if (config.nthWeek && config.weekdayOfMonth !== undefined) {
                const weekday = weekDays.find(w => w.value === config.weekdayOfMonth)?.full || '';
                text = `Every ${interval} month(s) on ${config.nthWeek} ${weekday}`;
            }
        } else if (config.type === 'yearly') {
            if (config.month !== undefined && config.dayOfMonth) {
                const monthName = months[config.month] || '';
                text = `Every year on day ${config.dayOfMonth} of ${monthName}`;
            } else if (config.month !== undefined && config.nthWeek && config.weekdayOfMonth !== undefined) {
                const monthName = months[config.month] || '';
                const weekday = weekDays.find(w => w.value === config.weekdayOfMonth)?.full || '';
                text = `Every year on ${config.nthWeek} ${weekday} of ${monthName}`;
            }
        } else if (config.type === 'custom') {
            const freq = config.frequency || 'daily';
            const unit = freq === 'daily' ? 'day(s)' : freq === 'weekly' ? 'week(s)' : freq === 'monthly' ? 'month(s)' : 'year(s)';
            text = `Every ${interval} ${unit}`;
        }

        if (config.ends === 'after' && config.occurrences) {
            text += `, ${config.occurrences} time(s)`;
        } else if (config.ends === 'on' && config.endDate) {
            text += `, until ${moment(config.endDate).format('MMM D, YYYY')}`;
        }

        return text;
    };

    const weekDays = [
        { label: 'S', value: 0, full: 'Sunday' },
        { label: 'M', value: 1, full: 'Monday' },
        { label: 'T', value: 2, full: 'Tuesday' },
        { label: 'W', value: 3, full: 'Wednesday' },
        { label: 'T', value: 4, full: 'Thursday' },
        { label: 'F', value: 5, full: 'Friday' },
        { label: 'S', value: 6, full: 'Saturday' },
    ];

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    // Set initial form values when form instance is available
    useEffect(() => {
        if (formInstance && mappedRefType) {
            formInstance.setFieldsValue({
                refType: mappedRefType,
                refId: recordId,
                taskOwner: currentUser?._id || initialValues?.taskOwner,
                enableReminder: enableReminder,
                enableRepeat: enableRepeat,
                reminderMode: reminderMode,
                reminderDaysBefore: reminderDaysBefore,
                reminderTime: reminderTime,
                reminderAlertType: reminderAlertType,
                repeatType: repeatType,
                repeatInterval: repeatInterval,
                repeatEnds: repeatEnds,
                repeatOccurrences: repeatOccurrences,
                repeatEndDate: repeatEndDate,
                ...initialValues
            });
        }
    }, [formInstance, mappedRefType, recordId, initialValues, currentUser, enableReminder, enableRepeat, reminderMode, reminderDaysBefore, reminderTime, reminderAlertType, repeatType, repeatInterval, repeatEnds, repeatOccurrences, repeatEndDate]);

    return (
        <>
            <FormSetup
                config={config}
                open={open}
                onClose={onCancel}
                isModal={true}
                form={(form) => {
                    setFormInstance(form);
                }}
            />

        </>
    );
};

export default TaskFormModal;
